import Phaser from 'phaser';
import type { BanknoteInstance } from '@gameplay/banknotes/models/BanknoteTypes';

export interface BanknoteDimensions {
  width: number;
  height: number;
}

export interface BanknoteViewResult {
  container: Phaser.GameObjects.Container;
  hitArea: Phaser.GameObjects.Rectangle;
  banknoteId: string;
  width: number;
  height: number;
}

/**
 * Placeholder banknot görseli — katalog verisinden render eder.
 * Gerçek asset geldiğinde yalnızca bu katman değiştirilecek.
 */
export class BanknoteView {
  static create(
    scene: Phaser.Scene,
    x: number,
    y: number,
    banknote: BanknoteInstance,
    dimensions: BanknoteDimensions,
    interactive = false,
  ): BanknoteViewResult {
    const { width, height } = dimensions;
    const container = scene.add.container(x, y);

    const graphics = scene.add.graphics();
    const fillColor = Phaser.Display.Color.HSLToColor(banknote.visual.hue / 360, 0.45, 0.42).color;
    const borderColor = Phaser.Display.Color.HSLToColor(banknote.visual.hue / 360, 0.5, 0.3).color;
    const cornerRadius = Math.round(height * 0.09);
    const borderWidth = Math.max(3, Math.round(height * 0.025));

    graphics.fillStyle(fillColor, 1);
    graphics.lineStyle(borderWidth, borderColor, 1);
    graphics.fillRoundedRect(-width / 2, -height / 2, width, height, cornerRadius);
    graphics.strokeRoundedRect(-width / 2, -height / 2, width, height, cornerRadius);

    this.drawPattern(graphics, banknote.visual.patternId, width, height);

    const serialFontSize = Math.round(height * 0.155);
    const labelFontSize = Math.round(height * 0.2);
    const denominationFontSize = Math.round(height * 0.14);

    const serial = scene.add.text(0, height / 2 - serialFontSize * 0.9, banknote.serial, {
      fontFamily: 'Arial, sans-serif',
      fontSize: `${serialFontSize}px`,
      color: '#1a1a1a',
      fontStyle: 'bold',
    });
    serial.setOrigin(0.5);

    const label = scene.add.text(-width * 0.28, -height * 0.06, banknote.brandLabel, {
      fontFamily: 'Arial, sans-serif',
      fontSize: `${labelFontSize}px`,
      color: '#1a1a1a',
      fontStyle: 'bold',
    });
    label.setOrigin(0.5);

    const denomination = scene.add.text(width * 0.28, -height * 0.06, String(banknote.denomination), {
      fontFamily: 'Arial, sans-serif',
      fontSize: `${denominationFontSize}px`,
      color: '#1a1a1a',
      fontStyle: 'bold',
    });
    denomination.setOrigin(0.5);

    container.add([graphics, label, denomination, serial]);

    const hitArea = scene.add
      .rectangle(x, y, width, height, 0x000000, 0)
      .setStrokeStyle(0);

    if (interactive) {
      hitArea.setInteractive({ useHandCursor: true });
    } else {
      hitArea.disableInteractive();
    }

    return { container, hitArea, banknoteId: banknote.instanceId, width, height };
  }

  private static drawPattern(
    graphics: Phaser.GameObjects.Graphics,
    patternId: number,
    width: number,
    height: number,
  ): void {
    const lineWidth = Math.max(2, Math.round(height * 0.018));
    graphics.lineStyle(lineWidth, 0x1a1a1a, 0.4);

    const left = -width / 2;
    const top = -height / 2;

    if (patternId === 0) {
      graphics.strokeCircle(left + width * 0.14, top + height * 0.22, height * 0.09);
      return;
    }

    if (patternId === 1) {
      const size = height * 0.18;
      graphics.strokeRect(left + width * 0.08, top + height * 0.14, size, size);
      return;
    }

    if (patternId === 2) {
      graphics.lineBetween(
        left + width * 0.07,
        top + height * 0.28,
        left + width * 0.18,
        top + height * 0.1,
      );
      return;
    }

    graphics.strokeCircle(0, 0, height * 0.16);
  }
}
